"use client";

import { useState, useEffect, useCallback } from "react";
import { dmsApi } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Loader2, Users } from "lucide-react";

export type ResourceShareType = "file" | "folder";

export interface ShareRecord {
  _id: string;
  resourceType: ResourceShareType;
  resourceId: string;
  sharedWithUserId: string | { _id?: string; name?: string; email?: string };
  sharedByUserId?: string | { _id?: string; name?: string; email?: string };
  permissionLevel: "view" | "edit";
  createdAt?: string;
}

interface UserOption {
  _id: string;
  name: string;
  email?: string;
}

interface ShareResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceType: ResourceShareType;
  resourceId: string;
  /** Called after access is saved so the browser can refresh visibility badges. */
  onSaved?: () => void;
}

function shareUserId(u: ShareRecord["sharedWithUserId"]): string {
  if (typeof u === "object" && u && u._id) return String(u._id);
  return typeof u === "string" ? u : "";
}

function isShareOwnerOnlyError(err: unknown): boolean {
  const e = err as { status?: number; error?: string; message?: string };
  const msg = (e?.error ?? e?.message ?? "").toString().toLowerCase();
  return e?.status === 403 && (msg.includes("private folder") || msg.includes("owner"));
}

function getShareErrorMessage(err: unknown, fallback: string): string {
  if (isShareOwnerOnlyError(err)) return "Only folder owner can manage sharing.";
  const e = err as { error?: string; message?: string };
  return e?.error ?? e?.message ?? fallback;
}

export function ShareResourceDialog({
  open,
  onOpenChange,
  resourceType,
  resourceId,
  onSaved,
}: ShareResourceDialogProps) {
  const { user: currentUser } = useAuth();
  const myId =
    (currentUser as { id?: string; _id?: string })?.id ??
    (currentUser as { _id?: string })?._id ??
    "";

  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"department" | "restricted">("department");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [permissionLevel, setPermissionLevel] = useState<"view" | "edit">("view");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    try {
      const [shareData, userData, folderMeta] = await Promise.all([
        dmsApi.shares.listForResource(resourceType, resourceId),
        dmsApi.users.shareable(),
        resourceType === "folder" ? dmsApi.folders.get(resourceId).catch(() => null) : Promise.resolve(null),
      ]);

      const list = Array.isArray(userData) ? userData : [];
      setUsers(
        list.map((u: { _id: string; name?: string; email?: string }) => ({
          _id: u._id,
          name: u.name ?? "—",
          email: u.email,
        }))
      );

      const shares = Array.isArray(shareData) ? (shareData as ShareRecord[]) : [];
      const fromShares = new Set(
        shares.map((s) => shareUserId(s.sharedWithUserId)).filter((id) => id && id !== myId)
      );

      const isPrivate =
        resourceType === "folder" &&
        folderMeta &&
        (folderMeta as { visibility?: string }).visibility === "private";

      if (isPrivate || fromShares.size > 0) {
        setMode("restricted");
        setSelectedIds(fromShares);
        const levels = shares.map((s) => s.permissionLevel);
        setPermissionLevel(levels.includes("edit") ? "edit" : "view");
      } else {
        setMode("department");
        setSelectedIds(new Set());
        setPermissionLevel("view");
      }
      setSearch("");
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Error",
        description: getShareErrorMessage(err, "Failed to load access settings."),
      });
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId, myId]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const pickableUsers = users.filter((u) => u._id !== myId);
  const filteredUsers = pickableUsers.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      u.name.toLowerCase().includes(q) ||
      (u.email && u.email.toLowerCase().includes(q))
    );
  });

  const toggleUser = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredUsers.forEach((u) => next.add(u._id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleSave = async () => {
    const effectiveMode =
      mode === "restricted" && selectedIds.size === 0 ? "department" : mode;

    setSubmitting(true);
    try {
      await dmsApi.shares.setAccess({
        resourceType,
        resourceId,
        mode: effectiveMode,
        userIds: effectiveMode === "restricted" ? Array.from(selectedIds) : [],
        permissionLevel,
      });

      if (effectiveMode === "department") {
        toast({
          title: "Access updated",
          description: "Everyone in the department can access this item as usual.",
        });
      } else {
        toast({
          title: "Access updated",
          description:
            resourceType === "folder"
              ? `Only you and ${selectedIds.size} selected user(s) can access this folder.`
              : `Access granted to ${selectedIds.size} selected user(s).`,
        });
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err: unknown) {
      toast({
        variant: "destructive",
        title: "Could not update access",
        description: getShareErrorMessage(err, "Request failed."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Give access — {resourceType === "folder" ? "folder" : "file"}
          </DialogTitle>
          <DialogDescription>
            Choose who in your department can access this item. Leave as department-wide
            (normal) if you do not select specific people.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0">
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as "department" | "restricted")}
              className="space-y-3"
            >
              <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/30">
                <RadioGroupItem value="department" id="access-dept" className="mt-0.5" />
                <div className="space-y-0.5">
                  <div className="text-sm font-medium">Everyone in department</div>
                  <p className="text-xs text-muted-foreground">
                    Normal access — same as today. No specific people selected.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/30">
                <RadioGroupItem value="restricted" id="access-people" className="mt-0.5" />
                <div className="space-y-0.5">
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    <Users size={14} />
                    Specific people only
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {resourceType === "folder"
                      ? "Only you and the people you select can see this folder."
                      : "Grant access to selected people. Other department members may still see the file unless it is inside a private folder."}
                  </p>
                </div>
              </label>
            </RadioGroup>

            {mode === "restricted" && (
              <div className="space-y-3 rounded-lg border p-3 bg-muted/20">
                <div className="grid gap-2">
                  <Label>Permission for selected people</Label>
                  <Select
                    value={permissionLevel}
                    onValueChange={(v) => setPermissionLevel(v as "view" | "edit")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">View</SelectItem>
                      <SelectItem value="edit">Edit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>People in your department</Label>
                    <div className="flex gap-1">
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAllFiltered}>
                        Select all
                      </Button>
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={clearSelection}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  <Input
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="max-h-52 overflow-y-auto rounded-md border bg-background divide-y">
                    {filteredUsers.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-3">No users found.</p>
                    ) : (
                      filteredUsers.map((u) => {
                        const checked = selectedIds.has(u._id);
                        return (
                          <label
                            key={u._id}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => toggleUser(u._id, v === true)}
                            />
                            <span className="text-sm min-w-0">
                              <span className="font-medium block truncate">{u.name}</span>
                              {u.email ? (
                                <span className="text-xs text-muted-foreground truncate block">
                                  {u.email}
                                </span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedIds.size === 0
                      ? "No one selected — saving will keep department-wide (normal) access."
                      : `${selectedIds.size} person(s) selected.`}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
